// OTA content: maps the release's content bundles to Pixi Assets bundles. Pure functions; the
// loading itself is in remoteContent.ts.
import type { UnresolvedAsset } from "pixi.js";

/** One file of a content bundle, as it comes from the server config. */
export interface ContentAsset {
  alias: string;
  url: string;
  sha256: string;
  contentType?: string;
}

export type ContentBundles = Record<string, ContentAsset[]>;

export interface ContentDiff {
  /** New bundles, and bundles whose files changed. */
  changed: string[];
  removed: string[];
}

const BUNDLE_NAME = /^[a-z][a-zA-Z0-9-]{0,63}$/;
const SHA256 = /^[0-9a-f]{64}$/;
/** SVGs are rasterized on load; 2 keeps them sharp on high-DPI screens. */
const SVG_RESOLUTION = 2;

/** Reads `content.bundles`, dropping (with a warning) anything that doesn't match the contract. */
export function readContent(content: unknown): ContentBundles {
  const bundles = isObject(content) ? content.bundles : undefined;
  const out: ContentBundles = {};
  if (!isObject(bundles)) return out;
  for (const [name, assets] of Object.entries(bundles)) {
    if (!BUNDLE_NAME.test(name) || !Array.isArray(assets)) {
      console.warn(`content: ignoring bundle ${JSON.stringify(name)}`);
      continue;
    }
    const seen = new Set<string>();
    out[name] = assets.filter((asset: unknown): asset is ContentAsset => {
      const valid = isAsset(asset) && !seen.has(asset.alias);
      if (valid) seen.add(asset.alias);
      else console.warn(`content: ignoring an invalid asset in bundle ${name}`);
      return valid;
    });
  }
  return out;
}

/** Identifies a bundle's files: equal keys mean there is nothing to reload. */
export function bundleKey(assets: readonly ContentAsset[]): string {
  const entries = assets.map((asset) => `${asset.alias}=${asset.sha256}`);
  // oxlint-disable-next-line unicorn/no-array-sort -- sorts a fresh array; toSorted needs iOS 16
  return entries.sort().join(",");
}

/** Compares bundle keys (see bundleKey) with the next content. */
export function diffContent(previous: ReadonlyMap<string, string>, next: ContentBundles): ContentDiff {
  const changed = Object.keys(next).filter((name) => previous.get(name) !== bundleKey(next[name] ?? []));
  const removed = [...previous.keys()].filter((name) => !(name in next));
  return { changed, removed };
}

/**
 * Pixi registers every bundle alias globally too, so content aliases are namespaced by the Pixi
 * bundle id and never clash with the built-in manifest or an older version of the bundle.
 */
export function pixiAlias(bundleId: string, alias: string): string {
  return `${bundleId}/${alias}`;
}

/** The Pixi bundle assets for one content bundle registered as `bundleId`. */
export function toPixiAssets(bundleId: string, assets: readonly ContentAsset[]): UnresolvedAsset[] {
  return assets.map((asset) => {
    const out: UnresolvedAsset = { alias: pixiAlias(bundleId, asset.alias), src: asset.url };
    // URLs don't always end in an extension (e.g. dev storage), so the parser comes from the content type.
    const parser = parserFor(asset.contentType);
    if (parser) out.parser = parser;
    if (parser === "svg") out.data = { resolution: SVG_RESOLUTION };
    return out;
  });
}

/** The Pixi load parser for a MIME type, or undefined to let Pixi pick one from the URL. */
export function parserFor(contentType: string | undefined): string | undefined {
  const type = contentType?.split(";")[0]?.trim().toLowerCase() ?? "";
  if (type === "image/svg+xml") return "svg";
  if (["image/png", "image/jpeg", "image/webp", "image/avif"].includes(type)) return "texture";
  if (type === "application/json") return "json";
  if (type.startsWith("font/")) return "web-font";
  if (type.startsWith("text/")) return "text";
  return undefined;
}

function isAsset(value: unknown): value is ContentAsset {
  if (!isObject(value)) return false;
  const { alias, url, sha256, contentType } = value;
  return (
    typeof alias === "string" &&
    alias.length > 0 &&
    typeof url === "string" &&
    /^https?:\/\//.test(url) &&
    typeof sha256 === "string" &&
    SHA256.test(sha256) &&
    (contentType === undefined || typeof contentType === "string")
  );
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
